<template>
  <div
    class="relative"
    :class="hasError && 'has-error'"
    style="scroll-margin-top: calc(4rem + var(--sat))"
  >
    <label
      v-if="label"
      class="block px-6 mb-1 text-sm font-medium leading-5 text-gray-700"
    >
      {{ label }}
      {{ required ? '*' : '' }}
    </label>
    <div v-if="description" class="text-xs px-4 text-gray-500 mb-1 -mt-1">
      {{ description }}
    </div>
    <div
      class="rounded-xl shadow-sm bg-white border -my-px flex flex-col transition duration-150 ease-in-out"
      :class="[
        !isFocused && !errorMessage && 'border-gray-300',
        isFocused && !errorMessage && 'border-blue-300',
        errorMessage && 'border-red-400',
      ]"
    >
      <div
        v-if="placeholder && !modelValue"
        class="absolute pointer-events-none py-2.5 px-3 sm:text-sm sm:leading-5 text-gray-400"
      >
        {{ placeholder }}
      </div>
      <div
        ref="inputEl"
        v-memo="[disabled]"
        class="appearance-none rounded-xl overflow-y-auto bg-transparent block w-full py-2.5 px-3 placeholder-gray-400 focus:outline-none sm:text-sm sm:leading-5 whitespace-pre-wrap"
        :class="[disabled && 'pointer-events-none']"
        :style="{
          ...(maxHeight && { maxHeight: `${maxHeight}px` }),
          minHeight: `${20 + rows * 20}px`,
        }"
        :contenteditable="disabled ? undefined : true"
        v-bind="$attrs"
        v-on="handlers"
        @focus="isFocused = true"
        @click="onClick"
        @keypress="onKeypress"
        @keyup="onKeyUp"
        @keydown="onKeyDown"
        @blur="onBlur"
        v-text="modelValue"
      ></div>

      <slot />
    </div>
    <div class="flex pt-px text-sm leading-4">
      <transition
        enter-active-class="transition-all duration-300"
        enter-from-class="transform -translate-y-3 opacity-0"
        enter-to-class="transform translate-y-0 opacity-100"
        leave-active-class="transition-all duration-300"
        leave-from-class="transform translate-y-0"
        leave-to-class="transform -translate-y-3 opacity-0"
      >
        <span v-if="hasError" class="text-red-600 px-4 mt-1">
          <slot name="error">
            {{ errorMessage }}
          </slot>
        </span>
      </transition>
      <div
        v-if="maxLength && typeof modelValue === 'string'"
        class="flex-shrink-0 pl-6 ml-auto leading-5 text-gray-500"
      >
        <span>{{ modelValue.length }}</span>
        <span class="mx-px"> / </span>
        <span>{{ maxLength }}</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { PropType } from 'vue'
import type { RuleExpression } from 'vee-validate'
import { useSimpleInput } from './use-simple-input'
import { getCaretIndex, setCaretIndex } from '~/utils/caret-index'

const props = defineProps({
  modelValue: {
    type: [String, Number, Object] as PropType<string | number | null>,
    default: '',
  },
  label: {
    type: String,
    default: '',
  },
  description: {
    type: String,
    default: '',
  },
  placeholder: {
    type: String,
    default: '',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  name: {
    type: String,
    required: true,
  },
  rules: {
    type: [String, Function, Object] as PropType<
      RuleExpression<boolean | object | string | number | any[] | null>
    >,
    default: '',
  },
  rows: {
    type: Number,
    default: 4,
  },
  maxHeight: {
    type: [Number, Boolean],
    default: false,
  },
  error: {
    type: String,
    default: '',
  },
  maxLength: {
    type: Number,
    default: undefined,
  },
  required: {
    type: Boolean,
    default: false,
  },
  validateOnBlur: {
    type: Boolean,
    default: false,
  },
})

const emits = defineEmits(['update:modelValue', 'keypress', 'keyup', 'keydown'])
const isFocused = ref(false)
const caretPosition = ref(0)
const inputEl = ref<InstanceType<typeof HTMLInputElement> | null>(null)

const slots = useSlots()
const hasError = computed(
  () => !props.disabled && (errorMessage.value || slots.error)
)

watch(
  () => props.modelValue,
  (value) => {
    if (inputEl.value && value !== inputEl.value?.innerText) {
      inputEl.value.innerText = String(value)
      caretPosition.value = 0
    }

    handleChange(value, false)
  }
)

const {
  errorMessage,
  handleChange,
  validate,
  meta,
  resetField: resetSimpleInputField,
} = useSimpleInput(props, getCurrentInstance())

const handlers = {
  input(event: any) {
    const value = event.target?.innerText
    handleChange(value, false)
    emits('update:modelValue', value)
    meta.validated && validate()
  },
  blur() {
    props.validateOnBlur && validate()
  },
}

// paste looks ok and we don't want to support old IE, but let it be here for now

// function detectPaste(editable: any, callback: (data: any) => void) {
//   const isIE = /MSIE /i.test(navigator.userAgent)
//   const isEdge = /Edge\/\d./i.test(navigator.userAgent)

//   editable.addEventListener('paste', (e: any) => {
//     const clipboard = e.clipboardData

//     if (clipboard && !(isIE || isEdge)) {
//       const types = clipboard.types
//       const data = {} as { [key: string]: any }

//       e.preventDefault()

//       for (let i = 0; i < types.length; i++) {
//         data[types[i]] = clipboard.getData(types[i])
//       }

//       callback(data)
//     }
//   })
// }

function onBlur() {
  isFocused.value = false
}

function focus() {
  setTimeout(() => {
    inputEl.value?.focus()
  }, 100)
}

function onKeypress(event: KeyboardEvent) {
  const isEnter = ['Enter', '\n'].includes(event.key)

  if (isEnter && !event.shiftKey) {
    document.execCommand('insertLineBreak')
    event.preventDefault()
  }
  emits('keypress', event)
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function setCaretPosition(position: number) {
  caretPosition.value = position
  setCaretIndex(inputEl.value, position)
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function getCaretPosition() {
  return caretPosition.value
}

function onClick(event: any) {
  caretPosition.value = getCaretIndex(event.target)
}

function onKeyUp(event: any) {
  caretPosition.value = getCaretIndex(event.target)
  emits('keyup', event)
}

function onKeyDown(event: any) {
  caretPosition.value = getCaretIndex(event.target)
  emits('keydown', event)
}

defineExpose({
  setCaretIndex,
  getCaretIndex,
  focus,
  validate,
  resetField: () => {
    emits('update:modelValue', '')
    resetSimpleInputField()
  },
  meta,
})
</script>
